import { useState, useMemo } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateSource, useUpdateSource } from '@/hooks/useSources';
import { useToast } from '@/hooks/useToast';

/**
 * Supported source types and their configuration requirements.
 * These keys MUST match the database constraint:
 *   source_type IN ('file_upload', 's3', 'gcs', 'azure_blob', 'local_filesystem',
 *                   'database', 'api', 'stream', 'manual')
 */
const SOURCE_TYPES = {
  local_filesystem: {
    label: 'Local File System',
    description: 'Read from local directories',
    fields: [
      {
        name: 'path',
        label: 'Directory Path',
        type: 'text',
        placeholder: 'C:\\data\\my-files or /data/my-files',
        required: true,
        description: 'Path to the directory containing data files. Supports recursive scanning.',
      },
    ],
  },
  file_upload: {
    label: 'File Upload',
    description: 'Manual file uploads through the UI',
    fields: [
      {
        name: 'location',
        label: 'Storage Location',
        type: 'text',
        placeholder: '/data/uploads',
        required: false,
        description: 'Where uploaded files are stored',
      },
    ],
  },
  s3: {
    label: 'Amazon S3',
    description: 'Read from S3 buckets',
    fields: [
      {
        name: 'bucket',
        label: 'Bucket Name',
        type: 'text',
        placeholder: 'my-data-bucket',
        required: true,
      },
      {
        name: 'region',
        label: 'AWS Region',
        type: 'text',
        placeholder: 'us-east-1',
        required: true,
      },
      {
        name: 'prefix',
        label: 'Key Prefix',
        type: 'text',
        placeholder: 'data/',
        required: false,
        description: 'Optional prefix to filter objects',
      },
      {
        name: 'access_key_id',
        label: 'Access Key ID',
        type: 'password',
        placeholder: 'AKIA...',
        required: false,
        description: 'Leave empty to use instance role or environment credentials',
      },
      {
        name: 'secret_access_key',
        label: 'Secret Access Key',
        type: 'password',
        placeholder: '***',
        required: false,
      },
    ],
  },
  gcs: {
    label: 'Google Cloud Storage',
    description: 'Read from GCS buckets',
    fields: [
      {
        name: 'bucket',
        label: 'Bucket Name',
        type: 'text',
        placeholder: 'my-gcs-bucket',
        required: true,
      },
      {
        name: 'prefix',
        label: 'Object Prefix',
        type: 'text',
        placeholder: 'data/',
        required: false,
        description: 'Optional prefix to filter objects',
      },
      {
        name: 'credentials_json',
        label: 'Service Account JSON',
        type: 'textarea',
        // Deliberately not a JSON sample. Spelling out a GCP key's type field in
        // quoted-JSON form trips Trivy's gcp-service-account secret rule, which
        // security:container gates on (#1191) -- so writing the example here,
        // even as a comment, reddens the pipeline. The label above already names
        // the expected artifact.
        placeholder: 'Paste the full service account key file contents',
        required: false,
        description: 'Leave empty to use application default credentials',
      },
    ],
  },
  azure_blob: {
    label: 'Azure Blob Storage',
    description: 'Read from Azure Blob containers',
    fields: [
      {
        name: 'account_name',
        label: 'Storage Account Name',
        type: 'text',
        placeholder: 'mystorageaccount',
        required: true,
      },
      {
        name: 'container_name',
        label: 'Container Name',
        type: 'text',
        placeholder: 'my-container',
        required: true,
      },
      {
        name: 'prefix',
        label: 'Blob Prefix',
        type: 'text',
        placeholder: 'data/',
        required: false,
        description: 'Optional prefix to filter blobs',
      },
      {
        name: 'connection_string',
        label: 'Connection String',
        type: 'password',
        placeholder: 'DefaultEndpointsProtocol=https;AccountName=...',
        required: false,
        description: 'Leave empty to use managed identity',
      },
    ],
  },
  database: {
    label: 'Database',
    description: 'Connect to a database',
    fields: [
      {
        name: 'host',
        label: 'Host',
        type: 'text',
        placeholder: 'localhost',
        required: true,
      },
      {
        name: 'port',
        label: 'Port',
        type: 'number',
        placeholder: '5432',
        required: false,
      },
      {
        name: 'database',
        label: 'Database Name',
        type: 'text',
        placeholder: 'my_database',
        required: true,
      },
      {
        name: 'username',
        label: 'Username',
        type: 'text',
        placeholder: 'db_user',
        required: false,
      },
      {
        name: 'password',
        label: 'Password',
        type: 'password',
        placeholder: '***',
        required: false,
      },
    ],
  },
  api: {
    label: 'REST API',
    description: 'Connect to a REST API',
    fields: [
      {
        name: 'base_url',
        label: 'Base URL',
        type: 'text',
        placeholder: 'https://api.example.com/v1',
        required: true,
      },
      {
        name: 'auth_type',
        label: 'Authentication Type',
        type: 'select',
        options: ['none', 'api_key', 'bearer', 'basic'],
        required: false,
      },
      {
        name: 'api_key',
        label: 'API Key / Token',
        type: 'password',
        placeholder: '***',
        required: false,
        description: 'API key, bearer token, or basic auth credentials',
      },
    ],
  },
  stream: {
    label: 'Streaming',
    description: 'Connect to a streaming source (Kafka, etc.)',
    fields: [
      {
        name: 'endpoint',
        label: 'Endpoint',
        type: 'text',
        placeholder: 'kafka://localhost:9092',
        required: true,
      },
      {
        name: 'topic',
        label: 'Topic',
        type: 'text',
        placeholder: 'my-topic',
        required: false,
      },
      {
        name: 'consumer_group',
        label: 'Consumer Group',
        type: 'text',
        placeholder: 'my-consumer-group',
        required: false,
      },
    ],
  },
  manual: {
    label: 'Manual Entry',
    description: 'Manually entered data',
    fields: [
      {
        name: 'notes',
        label: 'Notes',
        type: 'textarea',
        placeholder: 'Describe the data source...',
        required: false,
      },
    ],
  },
} as const;

type SourceType = keyof typeof SOURCE_TYPES;

interface SourceModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  source?: {
    source_id?: string;
    id?: string;
    name?: string;
    source_type?: string;
    connection_config?: Record<string, any>;
    description?: string | null;
    is_active?: boolean;
    metadata?: Record<string, any>;
  };
}

export const SourceModal: React.FC<SourceModalProps> = ({
  open,
  onOpenChange,
  source,
}) => {
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [sourceType, setSourceType] = useState<SourceType | ''>('');
  const [connectionConfig, setConnectionConfig] = useState<Record<string, any>>({});
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);

  const createSource = useCreateSource();
  const updateSource = useUpdateSource();
  const { toast } = useToast();

  const isEditMode = !!source;
  const sourceId = source?.source_id || source?.id;

  // Get the fields for the currently selected source type
  const currentTypeConfig = useMemo(() => {
    if (!sourceType) return null;
    return SOURCE_TYPES[sourceType];
  }, [sourceType]);

  // Validate source name
  const validateSourceName = (sourceName: string): string => {
    if (!sourceName || sourceName.trim().length === 0) {
      return 'Source name is required';
    }
    if (!/^[a-zA-Z][a-zA-Z0-9_]*$/.test(sourceName)) {
      return 'Source name must start with a letter and contain only letters, numbers, and underscores (no hyphens or spaces)';
    }
    return '';
  };

  // Handle name change with validation
  const handleNameChange = (value: string) => {
    setName(value);
    setNameError(validateSourceName(value));
  };

  // Reset the form when the dialog opens/closes or the edited source changes.
  // Adjusted during render (React's documented pattern for resetting state
  // from props: https://react.dev/reference/react/useState#storing-information-from-previous-renders)
  // instead of in an effect: `hasSeeded`/`seededSource`/`seededOpen` mirror what
  // the form was last seeded from. A mismatch — including the first render —
  // seeds the form synchronously and updates the mirrors, so React re-renders
  // once more before committing and the branch converges (no further updates).
  const [hasSeeded, setHasSeeded] = useState(false);
  const [seededSource, setSeededSource] = useState(source);
  const [seededOpen, setSeededOpen] = useState(open);
  if (!hasSeeded || source !== seededSource || open !== seededOpen) {
    setHasSeeded(true);
    setSeededSource(source);
    setSeededOpen(open);
    if (source) {
      setName(source.name || '');
      setNameError('');
      setSourceType((source.source_type as SourceType) || '');
      setConnectionConfig(source.connection_config || {});
      setDescription(source.description || '');
      setIsActive(source.is_active ?? true);
    } else {
      // Reset form for create mode
      setName('');
      setNameError('');
      setSourceType('');
      setConnectionConfig({});
      setDescription('');
      setIsActive(true);
    }
  }

  // Update a single field in the connection config
  const updateConfigField = (fieldName: string, value: any) => {
    setConnectionConfig((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  // Handle source type change - reset connection config
  const handleSourceTypeChange = (newType: SourceType) => {
    setSourceType(newType);
    // Only reset config if changing types (not on initial load)
    if (sourceType !== newType && sourceType !== '') {
      setConnectionConfig({});
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate name
    const nameValidationError = validateSourceName(name);
    if (nameValidationError) {
      setNameError(nameValidationError);
      toast({
        title: 'Validation Error',
        description: nameValidationError,
        variant: 'destructive',
      });
      return;
    }

    if (!sourceType) {
      toast({
        title: 'Error',
        description: 'Please select a source type',
        variant: 'destructive',
      });
      return;
    }

    // Build the API payload matching the backend DataSourceCreate model
    const payload = {
      name,
      source_type: sourceType,
      connection_config: connectionConfig,
      description: description || undefined,
      is_active: isActive,
      metadata: {},
    };

    try {
      if (isEditMode && sourceId) {
        await updateSource.mutateAsync({ id: sourceId, data: payload });
        toast({
          title: 'Success',
          description: 'Data source updated successfully',
          variant: 'success',
        });
      } else {
        await createSource.mutateAsync(payload);
        toast({
          title: 'Success',
          description: 'Data source created successfully',
          variant: 'success',
        });
      }
      onOpenChange(false);
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message || 'Failed to save data source',
        variant: 'destructive',
      });
    }
  };

  const renderConfigField = (field: (typeof SOURCE_TYPES)[SourceType]['fields'][number]) => {
    const value = connectionConfig[field.name] || '';

    if (field.type === 'select' && 'options' in field) {
      return (
        <Select
          value={value}
          onValueChange={(v) => updateConfigField(field.name, v)}
        >
          <SelectTrigger>
            <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt.charAt(0).toUpperCase() + opt.slice(1).replace('_', ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      );
    }

    if (field.type === 'textarea') {
      return (
        <textarea
          id={field.name}
          value={value}
          onChange={(e) => updateConfigField(field.name, e.target.value)}
          placeholder={field.placeholder}
          required={field.required}
          className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
        />
      );
    }

    return (
      <Input
        id={field.name}
        type={field.type === 'number' ? 'number' : field.type === 'password' ? 'password' : 'text'}
        value={value}
        onChange={(e) =>
          updateConfigField(
            field.name,
            field.type === 'number' ? (e.target.value ? parseInt(e.target.value, 10) : '') : e.target.value
          )
        }
        placeholder={field.placeholder}
        required={field.required}
      />
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>
              {isEditMode ? 'Edit Data Source' : 'Add Data Source'}
            </DialogTitle>
            <DialogDescription>
              {isEditMode
                ? 'Update the data source configuration'
                : 'Configure a new data source connection'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            {/* Name field */}
            <div className="grid gap-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => handleNameChange(e.target.value)}
                placeholder="e.g., customer_data_source"
                className={nameError ? 'border-destructive' : ''}
                required
              />
              {nameError && (
                <p className="text-sm text-destructive">{nameError}</p>
              )}
              <p className="text-xs text-muted-foreground">
                Must start with a letter. Only letters, numbers, and underscores allowed
              </p>
            </div>

            {/* Source Type dropdown */}
            <div className="grid gap-2">
              <Label htmlFor="type">Source Type *</Label>
              <Select
                value={sourceType}
                onValueChange={(v) => handleSourceTypeChange(v as SourceType)}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select a source type" />
                </SelectTrigger>
                <SelectContent>
                  {(Object.entries(SOURCE_TYPES) as [SourceType, typeof SOURCE_TYPES[SourceType]][]).map(
                    ([key, config]) => (
                      <SelectItem key={key} value={key}>
                        <div className="flex flex-col">
                          <span>{config.label}</span>
                          <span className="text-xs text-muted-foreground">
                            {config.description}
                          </span>
                        </div>
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>

            {/* Dynamic configuration fields based on source type */}
            {currentTypeConfig && (
              <div className="space-y-4 border rounded-lg p-4 bg-muted/30">
                <h4 className="text-sm font-medium">
                  {currentTypeConfig.label} Configuration
                </h4>
                {currentTypeConfig.fields.map((field) => (
                  <div key={field.name} className="grid gap-2">
                    <Label htmlFor={field.name}>
                      {field.label}
                      {field.required && ' *'}
                    </Label>
                    {renderConfigField(field)}
                    {'description' in field && field.description && (
                      <p className="text-xs text-muted-foreground">
                        {field.description}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Description field */}
            <div className="grid gap-2">
              <Label htmlFor="description">Description</Label>
              <Input
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>

            {/* Active status (only show in edit mode) */}
            {isEditMode && (
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="h-4 w-4 rounded border-border"
                />
                <Label htmlFor="is_active" className="text-sm font-normal">
                  Active
                </Label>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={createSource.isPending || updateSource.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createSource.isPending || updateSource.isPending || !sourceType}
            >
              {createSource.isPending || updateSource.isPending
                ? 'Saving...'
                : isEditMode
                ? 'Update'
                : 'Create'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
